import { ABILITY_NAMES } from '@shared/rules/constants'
import type { SpellcastingSummary } from '@shared/rules/calc'
import { formatModifier } from '@shared/rules/calc'
import { SPELLS_BY_ID } from '@shared/rules/data/spells'
import { Button, Panel, SectionHeading } from '@/components/ui'
import { cn } from '@/lib/cn'

const ordinal = (n: number): string => (n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th')

export function SpellcastingPanel({
  spellcasting, slotsUsed, concentratingOn, canEdit, onSlotsChange, onConcentrationChange,
}: {
  spellcasting: SpellcastingSummary
  slotsUsed: Record<string, number>
  concentratingOn: string | null
  canEdit: boolean
  onSlotsChange: (next: Record<string, number>) => void
  onConcentrationChange: (spellId: string | null) => void
}) {
  const levels = Object.entries(spellcasting.slots)
    .map(([level, count]) => [Number(level), count] as const)
    .sort((a, b) => a[0] - b[0])

  const concentratedSpell = concentratingOn ? SPELLS_BY_ID.get(concentratingOn) : null

  /** Clicking pip N sets usage to N, or clears back to N-1 if it was already spent. */
  function setUsed(level: number, index: number) {
    const current = slotsUsed[String(level)] ?? 0
    const next = current >= index ? index - 1 : index
    onSlotsChange({ ...slotsUsed, [String(level)]: Math.max(0, next) })
  }

  const anySpent = Object.values(slotsUsed).some((n) => n > 0)

  return (
    <Panel className="space-y-4 p-4">
      <SectionHeading
        action={canEdit && anySpent && (
          <Button size="sm" variant="ghost" onClick={() => onSlotsChange({})}>Reset slots</Button>
        )}
      >
        Spellcasting
      </SectionHeading>

      <div className="grid grid-cols-3 gap-2.5">
        <Figure label="Ability" value={ABILITY_NAMES[spellcasting.ability].slice(0, 3)} />
        <Figure label="Save DC" value={spellcasting.saveDC} />
        <Figure label="Attack" value={formatModifier(spellcasting.attackBonus)} />
      </div>

      <div>
        <p className="mb-2 text-[10px] uppercase tracking-wider text-ink-500">
          {spellcasting.pactSlotLevel ? 'Pact magic slots' : 'Spell slots'}
          {canEdit && <span className="ml-2 normal-case tracking-normal">— tap to spend</span>}
        </p>

        {levels.length === 0 ? (
          <p className="text-sm text-ink-500">No slots at this level yet.</p>
        ) : (
          <div className="space-y-1.5">
            {levels.map(([level, total]) => {
              const used = Math.min(total, slotsUsed[String(level)] ?? 0)
              return (
                <div key={level} className="flex items-center gap-3">
                  <span className="w-10 shrink-0 font-mono text-xs text-ink-500">
                    {level}{ordinal(level)}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {Array.from({ length: total }, (_, i) => i + 1).map((index) => {
                      const spent = index <= used
                      const props = {
                        className: cn(
                          'size-5 rounded-md border transition-colors',
                          spent
                            ? 'border-ink-600 bg-ink-800'
                            : 'border-arcane-500/60 bg-arcane-900/60 shadow-[0_0_8px_-2px] shadow-arcane-500/50',
                        ),
                        title: spent ? `${level}${ordinal(level)} slot — spent` : `${level}${ordinal(level)} slot — available`,
                      }
                      return canEdit ? (
                        <button key={index} type="button" aria-pressed={spent}
                          aria-label={`${level}${ordinal(level)} level slot ${index}`}
                          onClick={() => setUsed(level, index)} {...props} />
                      ) : (
                        <span key={index} {...props} />
                      )
                    })}
                  </div>
                  <span className="ml-auto shrink-0 font-mono text-xs text-ink-500">
                    {total - used}/{total}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="border-t border-ink-700/60 pt-3">
        <p className="mb-1.5 text-[10px] uppercase tracking-wider text-ink-500">Concentration</p>
        {concentratedSpell ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-gold-600/50 bg-gold-900/40 px-2.5 py-1 text-sm text-gold-400">
              <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-gold-500" />
              {concentratedSpell.name}
            </span>
            {canEdit && (
              <Button size="sm" variant="ghost" onClick={() => onConcentrationChange(null)}>Drop it</Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-ink-500">
            Not concentrating on anything.
            {canEdit && ' Start one from a spell card below.'}
          </p>
        )}
      </div>
    </Panel>
  )
}

function Figure({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stat-slab gap-1">
      <span className="font-mono text-lg tabular-nums text-arcane-400">{value}</span>
      <span className="text-[10px] uppercase tracking-wider text-ink-500">{label}</span>
    </div>
  )
}
